import { db } from "../config/firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
  limit,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { subDays, startOfDay, endOfDay } from "date-fns";

class FirebaseAnalyticsService {
  constructor() {
    // Toggle between dev and production collections
    this.isDev = false; // Set to false for production (your data is in production collections)
  }

  getCollectionName(baseName) {
    return this.isDev ? `${baseName}_dev` : baseName;
  }

  /**
   * Test Firebase connection
   */
  async testConnection() {
    try {
      console.log("🔍 Testing Firebase connection...");
      const testQuery = query(
        collection(db, this.getCollectionName("daily_analytics")),
        limit(1)
      );
      const testSnapshot = await getDocs(testQuery);
      console.log("✅ Firebase connection successful");
      return true;
    } catch (error) {
      console.error("❌ Firebase connection failed:", error);
      return false;
    }
  }

  /**
   * List all available collections (for debugging)
   */
  async listCollections() {
    try {
      const collections = [
        "daily_analytics",
        "booking_events",
        "signup_events",
        "user_events",
        "screen_aggregates",
        "club_view_counts",
        "availability_search",
        "slider_tap",
      ];

      const results = {};

      for (const collectionName of collections) {
        try {
          const fullName = this.getCollectionName(collectionName);
          const q = query(collection(db, fullName), limit(1));
          const snapshot = await getDocs(q);
          results[fullName] = {
            exists: true,
            count: snapshot.size,
            hasData: !snapshot.empty,
          };
        } catch (error) {
          results[collectionName] = {
            exists: false,
            error: error.message,
          };
        }
      }

      console.log("📊 Collection status:", results);
      return results;
    } catch (error) {
      console.error("❌ Error listing collections:", error);
      return {};
    }
  }

  /**
   * Get daily analytics for a specific date range
   */
  async getDailyAnalytics(days = 30) {
    try {
      const collectionName = this.getCollectionName("daily_analytics");
      console.log(`🔍 Fetching daily analytics for last ${days} days...`);
      console.log(`📁 Querying collection: ${collectionName}`);

      const q = query(
        collection(db, collectionName),
        orderBy("__name__", "desc"),
        limit(days)
      );

      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        console.log(`📄 Document ${doc.id}:`, {
          unique_users: docData.unique_users?.length || 0,
          total_sessions: docData.total_sessions || 0,
          signups: docData.signups || 0,
          bookings_started: docData.bookings_started || 0,
          bookings_completed: docData.bookings_completed || 0,
        });
        data.push({
          id: doc.id,
          ...docData,
        });
      });

      console.log(`📊 Found ${data.length} daily analytics documents`);
      if (data.length === 0) {
        console.warn(`⚠️ No data found in collection: ${collectionName}`);
        console.warn(
          `💡 TIP: Check if your Flutter app is using the same environment mode (isDev: ${this.isDev})`
        );
      }
      return data;
    } catch (error) {
      console.error("❌ Error getting daily analytics:", error);
      console.error("❌ Error details:", error.message);
      return [];
    }
  }

  /**
   * Get booking events within date range
   */
  async getBookingEvents(days = 30, limitCount = 500) {
    try {
      console.log(`🔍 Fetching booking events for last ${days} days...`);

      const startDate = subDays(new Date(), days);

      const q = query(
        collection(db, this.getCollectionName("booking_events")),
        orderBy("timestamp", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const timestamp = docData.timestamp?.toDate();

        // Filter by date range
        if (timestamp && timestamp >= startDate) {
          data.push({
            id: doc.id,
            ...docData,
            timestamp: timestamp,
          });
        }
      });

      console.log(`📊 Found ${data.length} booking events`);
      return data;
    } catch (error) {
      console.error("❌ Error getting booking events:", error);
      return [];
    }
  }

  /**
   * Get signup events
   */
  async getSignupEvents(days = 30, limitCount = 500) {
    try {
      console.log(`🔍 Fetching signup events for last ${days} days...`);

      const startDate = subDays(new Date(), days);

      const q = query(
        collection(db, this.getCollectionName("signup_events")),
        orderBy("timestamp", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const timestamp = docData.timestamp?.toDate();

        if (timestamp && timestamp >= startDate) {
          data.push({
            id: doc.id,
            ...docData,
            timestamp: timestamp,
          });
        }
      });

      console.log(`📊 Found ${data.length} signup events`);
      return data;
    } catch (error) {
      console.error("❌ Error getting signup events:", error);
      return [];
    }
  }

  /**
   * Get screen engagement data
   */
  async getScreenEngagementData() {
    try {
      const q = query(
        collection(db, this.getCollectionName("screen_aggregates"))
      );
      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        data.push({
          id: doc.id,
          screenName: docData.screen_name || "Unknown",
          totalTimeSeconds: docData.total_time_seconds || 0,
          totalTimeMinutes: Math.round((docData.total_time_seconds || 0) / 60),
          totalSessions: docData.total_sessions || 0,
          averageSessionSeconds: docData.average_session_time_seconds || 0,
          averageSessionMinutes: Math.round(
            (docData.average_session_time_seconds || 0) / 60
          ),
          lastUpdated: docData.last_updated?.toDate?.() || null,
          engagementLevel: this.calculateEngagementLevel(docData),
        });
      });

      return data.sort((a, b) => b.totalTimeSeconds - a.totalTimeSeconds);
    } catch (error) {
      console.error("Error getting screen engagement data:", error);
      return [];
    }
  }

  /**
   * Calculate metrics for specific time periods (1d, 7d, 30d)
   */
  calculatePeriodMetrics(dailyAnalytics, bookingEvents, screenAnalytics) {
    const now = new Date();
    const periods = {
      "1d": 1,
      "7d": 7,
      "30d": 30,
    };

    const metrics = {};

    // Debug: Log what we're looking for vs what we have
    console.log("🔍 Date filtering debug:");
    console.log("  Current date:", this.formatDateToYYYYMMDD(now));
    console.log(
      "  Available daily analytics dates:",
      dailyAnalytics.map((d) => d.id)
    );

    Object.entries(periods).forEach(([key, days]) => {
      const cutoffDate = subDays(now, days);
      const cutoffDateString = this.formatDateToYYYYMMDD(cutoffDate);

      console.log(`  ${key} period: looking for dates >= ${cutoffDateString}`);

      // Filter daily analytics for this period
      let periodDailyData = dailyAnalytics.filter(
        (day) => day.id >= cutoffDateString
      );

      // TEMPORARY FIX: If no recent data, use all available data for testing
      if (periodDailyData.length === 0 && dailyAnalytics.length > 0) {
        console.log(
          `  ⚠️ No recent data for ${key} period, using all available data for testing`
        );
        periodDailyData = dailyAnalytics;
      }

      console.log(
        `  ${key} period: found ${periodDailyData.length} matching documents`
      );

      // Calculate unique users
      const uniqueUsersSet = new Set();
      let totalSessions = 0;

      periodDailyData.forEach((day) => {
        const users = day.unique_users || [];
        if (Array.isArray(users)) {
          users.forEach((userId) => uniqueUsersSet.add(userId));
        }
        totalSessions += day.total_sessions || 0;
      });

      // Filter booking events for this period
      const periodBookingEvents = bookingEvents.filter((event) => {
        const eventDate = event.timestamp;
        return eventDate && eventDate >= cutoffDate;
      });

      const bookingsStarted = periodBookingEvents.filter(
        (e) => e.event_type === "booking_started"
      ).length;
      const bookingsCompleted = periodBookingEvents.filter(
        (e) => e.event_type === "booking_completed"
      ).length;

      metrics[key] = {
        uniqueUsers: uniqueUsersSet.size,
        sessions: totalSessions,
        bookingsStarted,
        bookingsCompleted,
        conversionRate:
          bookingsStarted > 0
            ? ((bookingsCompleted / bookingsStarted) * 100).toFixed(1)
            : 0,
      };
    });

    // Calculate average engagement time for 30 days
    const totalEngagementSeconds = screenAnalytics.reduce(
      (sum, screen) => sum + screen.totalTimeSeconds,
      0
    );
    const totalSessions = metrics["30d"].sessions;
    const avgEngagementSeconds =
      totalSessions > 0 ? totalEngagementSeconds / totalSessions : 0;

    metrics.averageEngagementTime =
      this.formatEngagementTime(avgEngagementSeconds);

    return metrics;
  }

  /**
   * Get comprehensive dashboard data
   */
  async getDashboardData() {
    try {
      console.log("🚀 Starting dashboard data fetch...");
      console.log("🔧 Environment mode: isDev =", this.isDev);
      console.log("📁 Collection names:");
      console.log(
        "  - daily_analytics:",
        this.getCollectionName("daily_analytics")
      );
      console.log(
        "  - booking_events:",
        this.getCollectionName("booking_events")
      );
      console.log(
        "  - signup_events:",
        this.getCollectionName("signup_events")
      );
      console.log(
        "  - screen_aggregates:",
        this.getCollectionName("screen_aggregates")
      );

      const [
        screenAnalytics,
        dailyAnalytics,
        bookingEvents,
        signupEvents,
        clubViews,
        availabilitySearch,
        sliderTaps,
      ] = await Promise.all([
        this.getScreenEngagementData(),
        this.getDailyAnalytics(30),
        this.getBookingEvents(30),
        this.getSignupEvents(30),
        this.getClubViews(20),
        this.getAvailabilitySearch(),
        this.getSliderTaps(20),
      ]);

      // Calculate period metrics
      const periodMetrics = this.calculatePeriodMetrics(
        dailyAnalytics,
        bookingEvents,
        screenAnalytics
      );

      console.log("✅ Dashboard data fetch completed");
      console.log("📊 Data Summary:");
      console.log("  - Daily Analytics:", dailyAnalytics.length, "documents");
      console.log("  - Booking Events:", bookingEvents.length, "events");
      console.log("  - Signup Events:", signupEvents.length, "events");
      console.log("  - Screen Analytics:", screenAnalytics.length, "screens");
      console.log("  - Club Views:", clubViews.length, "clubs");
      console.log("  - Slider Taps:", sliderTaps.length, "sliders");
      console.log("📊 Period Metrics:", periodMetrics);

      return {
        screenAnalytics,
        dailyAnalytics,
        bookingEvents,
        signupEvents,
        clubViews,
        availabilitySearch,
        sliderTaps,
        periodMetrics,
        summary: this.getSummaryStatsFromData(screenAnalytics),
        _debug: {
          isDev: this.isDev,
          collectionsQueried: {
            daily_analytics: this.getCollectionName("daily_analytics"),
            booking_events: this.getCollectionName("booking_events"),
            signup_events: this.getCollectionName("signup_events"),
            screen_aggregates: this.getCollectionName("screen_aggregates"),
          },
          documentCounts: {
            dailyAnalytics: dailyAnalytics.length,
            bookingEvents: bookingEvents.length,
            signupEvents: signupEvents.length,
            screenAnalytics: screenAnalytics.length,
          },
        },
      };
    } catch (error) {
      console.error("❌ Error getting dashboard data:", error);
      throw error;
    }
  }

  /**
   * Get club views
   */
  async getClubViews(limitCount = 20) {
    try {
      const q = query(
        collection(db, this.getCollectionName("club_view_counts")),
        orderBy("view_count", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        data.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return data;
    } catch (error) {
      console.error("Error getting club views:", error);
      return [];
    }
  }

  /**
   * Get availability search data
   */
  async getAvailabilitySearch() {
    try {
      const docRef = doc(
        db,
        this.getCollectionName("availability_search"),
        "overall"
      );
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error getting availability search:", error);
      return null;
    }
  }

  /**
   * Get slider taps
   */
  async getSliderTaps(limitCount = 20) {
    try {
      const q = query(
        collection(db, this.getCollectionName("slider_tap")),
        orderBy("total_taps", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const data = [];

      querySnapshot.forEach((doc) => {
        data.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return data;
    } catch (error) {
      console.error("Error getting slider taps:", error);
      return [];
    }
  }

  // Helper methods

  calculateEngagementLevel(item) {
    const avgTime = item.average_session_time_seconds || 0;
    const sessions = item.total_sessions || 0;

    if (avgTime >= 300 && sessions >= 50) return "Very High";
    if (avgTime >= 120 && sessions >= 25) return "High";
    if (avgTime >= 60 && sessions >= 10) return "Medium";
    if (avgTime >= 30 && sessions >= 5) return "Low";
    return "Very Low";
  }

  getEngagementColor(level) {
    const colors = {
      "Very High": "#10B981",
      High: "#3B82F6",
      Medium: "#F59E0B",
      Low: "#EF4444",
      "Very Low": "#6B7280",
    };
    return colors[level] || "#6B7280";
  }

  calculateConversionRate(started, completed) {
    if (started === 0) return "0.0";
    return ((completed / started) * 100).toFixed(1);
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  }

  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  }

  formatDateToYYYYMMDD(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  formatEngagementTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  getSummaryStatsFromData(screenAnalytics) {
    const totalScreens = screenAnalytics.length;
    const totalTimeMinutes = screenAnalytics.reduce(
      (sum, item) => sum + item.totalTimeMinutes,
      0
    );
    const totalSessions = screenAnalytics.reduce(
      (sum, item) => sum + item.totalSessions,
      0
    );
    const averageTimePerScreen =
      totalScreens > 0 ? Math.round(totalTimeMinutes / totalScreens) : 0;

    return {
      totalScreens,
      totalTimeMinutes,
      totalSessions,
      averageTimePerScreen,
      mostPopularScreen: screenAnalytics[0]?.screenName || "N/A",
    };
  }
}

export default new FirebaseAnalyticsService();
